import { sleep } from "@/lib/utils";

import type {
	Api,
	FaqArticle,
	SupportTicket,
	SupportTicketDetail,
	SupportTicketMessage,
	SupportTicketStatus,
} from "../types";

// Офлайн-данные центра помощи (Э5, fd-support-01) — чтобы страница жила без
// бэкенда: несколько FAQ-статей по категориям (в т.ч. черновик) и обращения
// разных стадий с перепиской. Форма в точности повторяет DTO из
// @osmix/contracts/support, который отдаёт api-gateway.
const DAY = 86_400_000;
const at = (offsetDays: number, offsetHours = 0) =>
	new Date(Date.now() + offsetDays * DAY + offsetHours * 3_600_000).toISOString();

let nextId = 1;
const mintId = (prefix: string) => `${prefix}_demo_${nextId++}`;

let faq: FaqArticle[] = [
	{
		id: "faq_demo_pay_card",
		category: "Оплата",
		question: "Какими картами можно оплатить заказ?",
		answer: "Мы принимаем карты «Мир», Visa и Mastercard российских банков. Оплата проходит через защищённый шлюз, данные карты не хранятся в магазине.",
		sortOrder: 10,
		published: true,
		createdAt: at(-30),
		updatedAt: at(-30),
	},
	{
		id: "faq_demo_pay_split",
		category: "Оплата",
		question: "Можно ли оплатить заказ частями?",
		answer: "Оплата частями пока недоступна. Мы работаем над подключением рассрочки и сообщим, когда она появится.",
		sortOrder: 20,
		published: true,
		createdAt: at(-25),
		updatedAt: at(-25),
	},
	{
		id: "faq_demo_delivery_terms",
		category: "Доставка",
		question: "Сколько идёт доставка?",
		answer: "Срок зависит от магазина и региона. Обычно доставка по России занимает от 3 до 10 рабочих дней. Точный срок виден в карточке заказа.",
		sortOrder: 10,
		published: true,
		createdAt: at(-20),
		updatedAt: at(-4),
	},
	{
		id: "faq_demo_return_how",
		category: "Возвраты",
		question: "Как вернуть товар?",
		answer: "Откройте заказ, нажмите «Оформить возврат» и укажите причину. Заявку рассмотрит поддержка магазина в течение 4 рабочих дней.",
		sortOrder: 10,
		published: true,
		createdAt: at(-18),
		updatedAt: at(-18),
	},
	{
		id: "faq_demo_return_draft",
		category: "Возвраты",
		question: "Когда вернутся деньги за отменённый заказ?",
		answer: "Черновик статьи: уточнить у финансового отдела сроки возврата на карту (ожидаем 3–5 рабочих дней).",
		sortOrder: 20,
		published: false,
		createdAt: at(-2),
		updatedAt: at(-1),
	},
];

type MockTicket = SupportTicketDetail;

let tickets: MockTicket[] = [
	{
		id: "tkt_demo_0001",
		number: 1001,
		userId: "usr-maria",
		userName: "Мария Соколова",
		subject: "Не приходит SMS с кодом подтверждения",
		category: "Аккаунт",
		status: "open",
		messageCount: 1,
		createdAt: at(-1, -3),
		updatedAt: at(-1, -3),
		lastMessageAt: at(-1, -3),
		messages: [
			{
				id: "msg_demo_0001",
				ticketId: "tkt_demo_0001",
				authorUserId: "usr-maria",
				authorRole: "user",
				authorName: "Мария Соколова",
				attachments: [],
				body: "Пытаюсь войти в приложение, но SMS с кодом не приходит уже полчаса. Телефон +7 916 000-00-00.",
				createdAt: at(-1, -3),
			},
		],
	},
	{
		id: "tkt_demo_0002",
		number: 1002,
		userId: "usr-alexey",
		userName: "Алексей Кузнецов",
		subject: "Оплата прошла дважды",
		category: "Оплата",
		status: "in_progress",
		messageCount: 3,
		createdAt: at(-3),
		updatedAt: at(-1, -2),
		lastMessageAt: at(-1, -2),
		messages: [
			{
				id: "msg_demo_0010",
				ticketId: "tkt_demo_0002",
				authorUserId: "usr-alexey",
				authorRole: "user",
				authorName: "Алексей Кузнецов",
				attachments: [],
				body: "С карты списали сумму заказа два раза, а заказ в личном кабинете один. Помогите разобраться.",
				createdAt: at(-3),
			},
			{
				id: "msg_demo_0011",
				ticketId: "tkt_demo_0002",
				authorUserId: "support-1",
				authorRole: "support",
				authorName: "Поддержка OSMIX",
				attachments: [],
				body: "Здравствуйте! Проверяем платежи по вашему заказу. Уточните, пожалуйста, последние 4 цифры карты.",
				createdAt: at(-2),
			},
			{
				id: "msg_demo_0012",
				ticketId: "tkt_demo_0002",
				authorUserId: "usr-alexey",
				authorRole: "user",
				authorName: "Алексей Кузнецов",
				attachments: [],
				body: "Карта заканчивается на 4417.",
				createdAt: at(-1, -2),
			},
		],
	},
	{
		id: "tkt_demo_0003",
		number: 1003,
		userId: "usr-olga",
		userName: "Ольга Иванова",
		subject: "Как стать продавцом на площадке?",
		category: "Продавцам",
		status: "resolved",
		messageCount: 2,
		createdAt: at(-8),
		updatedAt: at(-6),
		lastMessageAt: at(-6),
		messages: [
			{
				id: "msg_demo_0020",
				ticketId: "tkt_demo_0003",
				authorUserId: "usr-olga",
				authorRole: "user",
				authorName: "Ольга Иванова",
				attachments: [],
				body: "Хочу продавать свои изделия. С чего начать?",
				createdAt: at(-8),
			},
			{
				id: "msg_demo_0021",
				ticketId: "tkt_demo_0003",
				authorUserId: "support-1",
				authorRole: "support",
				authorName: "Поддержка OSMIX",
				attachments: [],
				body: "Здравствуйте! Зарегистрируйтесь как продавец в разделе «Стать продавцом», заполните профиль магазина и загрузите документы — модерация занимает 1–2 дня.",
				createdAt: at(-6),
			},
		],
	},
	{
		id: "tkt_demo_0004",
		number: 1004,
		userId: "usr-dmitry",
		userName: "Дмитрий Волков",
		subject: "Спасибо за быструю доставку",
		category: "Другое",
		status: "closed",
		messageCount: 1,
		createdAt: at(-14),
		updatedAt: at(-13),
		lastMessageAt: at(-14),
		messages: [
			{
				id: "msg_demo_0030",
				ticketId: "tkt_demo_0004",
				authorUserId: "usr-dmitry",
				authorRole: "user",
				authorName: "Дмитрий Волков",
				attachments: [],
				body: "Просто хотел поблагодарить — заказ приехал раньше срока, всё в порядке.",
				createdAt: at(-14),
			},
		],
	},
];

const summary = (t: MockTicket): SupportTicket => {
	const { messages, ...rest } = t;
	void messages;
	return rest;
};

const find = (id: string): MockTicket => {
	const found = tickets.find((t) => t.id === id);
	if (!found) throw new Error(`Обращение «${id}» не найдено`);
	return found;
};

export const support: Api["support"] = {
	async faq() {
		await sleep(140);
		return [...faq].sort((a, b) =>
			a.category === b.category
				? a.sortOrder - b.sortOrder
				: a.category.localeCompare(b.category, "ru"),
		);
	},

	async createFaq(input) {
		await sleep(160);
		const now = at(0);
		const next: FaqArticle = {
			id: mintId("faq"),
			category: input.category?.trim() || "general",
			question: input.question,
			answer: input.answer,
			sortOrder: input.sortOrder ?? 0,
			published: input.published ?? false,
			createdAt: now,
			updatedAt: now,
		};
		faq = [...faq, next];
		return next;
	},

	async updateFaq(id, input) {
		await sleep(160);
		const prev = faq.find((a) => a.id === id);
		if (!prev) throw new Error(`Статья «${id}» не найдена`);
		const next: FaqArticle = {
			...prev,
			category: input.category ?? prev.category,
			question: input.question ?? prev.question,
			answer: input.answer ?? prev.answer,
			sortOrder: input.sortOrder ?? prev.sortOrder,
			published: input.published ?? prev.published,
			updatedAt: at(0),
		};
		faq = faq.map((a) => (a.id === id ? next : a));
		return next;
	},

	async deleteFaq(id) {
		await sleep(140);
		faq = faq.filter((a) => a.id !== id);
	},

	async tickets(status) {
		await sleep(160);
		const rows = status ? tickets.filter((t) => t.status === status) : tickets;
		return [...rows]
			.sort((a, b) => (a.lastMessageAt < b.lastMessageAt ? 1 : -1))
			.map(summary);
	},

	async ticket(id) {
		await sleep(140);
		return structuredClone(find(id));
	},

	async reply(id, input) {
		await sleep(200);
		const ticket = find(id);
		const message: SupportTicketMessage = {
			id: mintId("msg"),
			ticketId: id,
			authorUserId: "support-1",
			authorRole: "support",
			authorName: "Поддержка OSMIX",
			attachments: [],
			body: input.body,
			createdAt: at(0),
		};
		// Ответ поддержки двигает обращение в работу, если оно не решено/закрыто.
		const nextStatus: SupportTicketStatus =
			ticket.status === "resolved" || ticket.status === "closed" ? ticket.status : "in_progress";
		const next: MockTicket = {
			...ticket,
			messages: [...ticket.messages, message],
			messageCount: ticket.messageCount + 1,
			status: nextStatus,
			updatedAt: message.createdAt,
			lastMessageAt: message.createdAt,
		};
		tickets = tickets.map((t) => (t.id === id ? next : t));
		return structuredClone(next);
	},

	async setStatus(id, status) {
		await sleep(160);
		const ticket = find(id);
		const next: MockTicket = { ...ticket, status, updatedAt: at(0) };
		tickets = tickets.map((t) => (t.id === id ? next : t));
		return structuredClone(next);
	},

	async uploadAttachment(file) {
		await sleep(120);
		return {
			url: URL.createObjectURL(file),
			name: file.name,
			kind: file.type.startsWith("image/") ? "image" : "file",
		};
	},
};
